/**
 * #1016 — one read of a game's Polymarket US orders: every visible account's
 * own saved orders (the shared #1006 reader), the catalogue evidence that ties
 * each to a game, the cancel inventory of the exact game's orders, and (#1233)
 * the holdings the same order pages carry.
 *
 * Relative imports only: the browser-relay integration suite imports this
 * module under its own compiler, without the `@/` alias.
 */
import {
  readUsGameEvidence,
  selectUsGameOrders,
  US_GAME_MAX_SLUG_LOOKUPS,
  type UsGameOrderSelection,
  type UsSlugEvidence,
} from '../../lib/polymarketUs/gameOpenOrders';
import type { UsMarketClient } from '../../lib/polymarketUs/marketClient';
import type { UsPositionRow } from '../../lib/polymarketUs/types';
import {
  readUsOwnOpenOrders,
  usVisibleInventory,
  type UsBulkClients,
  type UsBulkExclusion,
  type UsBulkInventory,
  type UsBulkTarget,
} from '../PositionTracker/usBulkCancelModel';

export type UsGameReadClients = UsBulkClients & { markets: Pick<UsMarketClient, 'setSelection' | 'listMarkets'> };

/** #1233 — the trader's holdings from the same read, for the held snapshot.
 * Not narrowed to the game: the snapshot matches its own exact market. */
export type UsGameHoldings = {
  accounts: readonly { accountId: string; positions: readonly UsPositionRow[]; positionsError: string | null }[];
  /** Accounts whose read failed: their holdings are unknown. */
  failedAccounts: number;
};

/**
 * Read and select one game's US orders. The cancel inventory holds only the
 * exact-game orders with a current listing; their own permission and
 * readiness still decide each one (`usVisibleInventory`, as in Cancel all).
 */
export async function readUsGameOrders(
  clients: UsGameReadClients,
  usGameKey: string,
  clock: () => number,
  stillCurrent: () => boolean,
): Promise<{ selection: UsGameOrderSelection; inventory: UsBulkInventory; holdings: UsGameHoldings; readAtMs: number }> {
  const reads = await readUsOwnOpenOrders(clients, stillCurrent);
  const budget = { lookups: US_GAME_MAX_SLUG_LOOKUPS };
  const evidence = new Map<string, Map<string, UsSlugEvidence>>();
  // One account at a time on one market client: only its latest read wins.
  for (const read of reads) {
    if (!read.ok) continue;
    const slugs = [...new Set(read.orders.map((row) => row.target.marketSlug))];
    evidence.set(read.accountId, await readUsGameEvidence(clients.markets, { accountId: read.accountId, scope: read.scope },
      usGameKey, slugs, budget, stillCurrent));
  }
  const readAtMs = clock();
  const selection = selectUsGameOrders({ usGameKey, reads, evidence, nowMs: readAtMs });
  const targets: UsBulkTarget[] = [];
  const excluded: UsBulkExclusion[] = [];
  let accounts = 0;
  for (const account of selection.sweep) {
    const { inventory } = usVisibleInventory({ ...account, pending: new Set() }, readAtMs);
    targets.push(...inventory.targets);
    excluded.push(...inventory.excluded);
    accounts += inventory.accounts;
  }
  excluded.push(...selection.expired);
  const holdings: UsGameHoldings = {
    accounts: reads.flatMap((r) => (r.ok ? [{ accountId: r.accountId, positions: r.positions, positionsError: r.positionsError }] : [])),
    failedAccounts: reads.filter((r) => !r.ok).length,
  };
  return { selection, inventory: { targets, excluded, accounts }, holdings, readAtMs };
}
